import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Boiler } from '../atoms/icons/boiler';
import { ParentPaper } from '../atoms/parent-paper';
import { get, sum } from 'lodash';
import { ChartBox } from '../molecules/chart-box';
import { SliderBox } from '../molecules/slider-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { Group } from '../organisms/group';
import { useFormatNumber } from '../utils/format-number';
import { FormattedValue } from '../store/formatted-value';
import { FormattedStatus } from '../store/formatted-status';
import { useSelector } from '../store/utils';
import { Typography } from '@mui/material';
import { SettingsBoilerSpotTemp } from '../templates/settings/boxes/settings-boiler-spot-temp';
import { SettingsBoilerSpotBlocking } from '../templates/settings/boxes/settings-boiler-spot-blocking';

const BoilerPage = () => {
  const formatNumber = useFormatNumber();

  const value = useSelector((s) =>
    sum([
      get(s.variables, 'HW_INFO.Consumption[0]'),
      get(s.variables, 'HW_INFO.Consumption[1]'),
      get(s.variables, 'HW_INFO.Consumption[2]'),
    ])
  );

  const formattedValue = formatNumber(value, {
    style: 'unit',
    unit: 'kilowatt',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  const value2 = useSelector((s) =>
    get(s.variables, 'HW_ENERGY_PRODUCED_TOTAL')
  );

  const formattedValue2 = formatNumber(value2, {
    style: 'unit',
    unit: 'kilowatt-hour',
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });
  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="boiler.status"
              defaultMessage="Režim Bojleru"
            />
          }
          subtitle={<FormattedStatus type="heatWater" />}
          switchVariableId="HW_ON"
          fullWidth
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="boiler.statistics"
            defaultMessage="Výkon bojleru"
          />
        }
      >
        <ChartBox variableId="HW_POWER_GRAPH" />
      </Group>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="boiler.statisticsTemp"
            defaultMessage="Teplota bojleru"
          />
        }
        visibility={{ variableId: 'HW_TEMP_ENABLED' }}
      >
        <ChartBox variableId="HW_TEMP_GRAPH" />
      </Group>

      <Group
        title={
          <FormattedMessage
            id="boiler.common"
            defaultMessage="Obecné informace"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="boiler.common.temperature"
              defaultMessage="Aktuální teplota"
            />
          }
          value={
            <FormattedValue
              variableId="HW_TEMP"
              opts={{
                style: 'unit',
                unit: 'celsius',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          visibility={{ variableId: 'HW_TEMP_ENABLED' }}
          icon={<Boiler />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="boiler.common.power"
              defaultMessage="Aktuální odběr"
            />
          }
          value={formattedValue}
          icon={<Boiler />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="boiler.common.power.phase_1"
                defaultMessage="Fáze 1:"
              />
              &nbsp;
              <FormattedValue
                variableId="HW_INFO.Consumption[0]"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="boiler.common.power.phase_2"
                defaultMessage="Fáze 2:"
              />
              &nbsp;
              <FormattedValue
                variableId="HW_INFO.Consumption[1]"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="boiler.common.phase_3"
                defaultMessage="Fáze 3:"
              />
              &nbsp;
              <FormattedValue
                variableId="HW_INFO.Consumption[2]"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
            </Typography>
          }
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="boiler.common.total"
              defaultMessage="Celková spotřeba"
            />
          }
          value={formattedValue2}
          icon={<Boiler />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="boiler.common.power_today"
                defaultMessage="Dnešní spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_HW_ENERGY_PRODUCED_TODAY"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="boiler.common.power_year"
                defaultMessage="Roční spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_HW_ENERGY_PRODUCED_YEAR"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
      </Group>

      <Group
        title={
          <FormattedMessage id="boiler.settings" defaultMessage="Nastavení" />
        }
        layout="list"
        visibility={{ variableId: 'HW_TEMP_ENABLED' }}
      >
        <ParentPaper>
          <SliderBox
            title={
              <FormattedMessage
                id="boiler.settings.temp"
                defaultMessage="Nastavená teplota"
              />
            }
            subtitle={
              <FormattedMessage
                id="boiler.settings.temp.subtitle"
                defaultMessage="Běžná teplota, která je k dispozici vždy"
              />
            }
            variableId="HW_SET_TEMP"
            visibility={{ variableId: 'HW_TEMP_ENABLED' }}
            max={85}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="boiler.settings.temp_ek"
                defaultMessage="Teplota z FVE pomocí EK"
              />
            }
            subtitle={
              <FormattedMessage
                id="boiler.settings.temp_ek.subtitle"
                defaultMessage="Předehřívací teplota, která je pouze z přebytků"
              />
            }
            variableId="HW_MAX_TEMP_EK"
            visibility={{ variableId: 'HW_TEMP_ENABLED' }}
            max={85}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
          />
          <SettingsBoilerSpotTemp />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="boiler.blocking"
            defaultMessage="Blokace ohřevu"
          />
        }
        layout="list"
        visibility={{ variableId: 'HW_TEMP_ENABLED' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="boiler.settings.hdo"
                defaultMessage="Řízení běžného ohřevu dle HDO časů"
              />
            }
            subtitle={
              <FormattedMessage
                id="boiler.settings.hdo.subtitle"
                defaultMessage="Časy pro HDO musí být nastaveny v obecném nastavení"
              />
            }
            switchVariableId={'HW_HDO'}
            fullWidth
          />
          <SettingsBoilerSpotBlocking />
          <SliderBox
            title={
              <FormattedMessage
                id="boiler.settings.min_soc"
                defaultMessage="Blokace ohřevu při výpadku sítě"
              />
            }
            subtitle={
              <FormattedMessage
                id="boiler.settings.min_soc.subtitle"
                defaultMessage="Nabití baterie, od kterého dojde k blokaci ohřevu při výpadku sítě"
              />
            }
            variableId="HW_SOC_STOP"
            visibility={{ variableId: 'PV_BATTERY' }}
            max={100}
            opts={{
              style: 'percent',
              maximumFractionDigits: 0,
            }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="boiler.settings.min_soc_utility"
                defaultMessage="Blokace ohřevu při dostupné síti"
              />
            }
            subtitle={
              <FormattedMessage
                id="boiler.settings.min_soc_utility.subtitle"
                defaultMessage="Nabití baterie, od kterého dojde k blokaci ohřevu"
              />
            }
            variableId="HW_SOC_STOP_UTILITY"
            visibility={{ variableId: 'PV_BATTERY' }}
            max={100}
            opts={{
              style: 'percent',
              maximumFractionDigits: 0,
            }}
          />
        </ParentPaper>
      </Group>
    </>
  );
};

export default BoilerPage;
